import { useEffect, useRef, useState } from "react";
import { MicOff, VideoOff } from "lucide-react";

/**
 * Renders a RealtimeKit participant's (or self's) camera into a <video>.
 * Re-attaches on `videoUpdate` so device switches and cam on/off are seamless.
 */
function useAttachVideo(ref, rtk) {
  useEffect(() => {
    const el = ref.current;
    if (!el || !rtk) return undefined;
    let boundTrack = null;
    const unbind = () => {
      if (!boundTrack) return;
      try { boundTrack.removeEventListener("unmute", onTrackEvent); boundTrack.removeEventListener("ended", onTrackEvent); } catch (e) { /* noop */ }
      boundTrack = null;
    };
    const attach = () => {
      const track = rtk.videoTrack;
      if (rtk.videoEnabled && track) {
        try {
          if (boundTrack !== track) {
            unbind();
            el.srcObject = new MediaStream([track]);
            boundTrack = track;
            track.addEventListener("unmute", onTrackEvent);
            track.addEventListener("ended", onTrackEvent);
          }
          el.play().catch(() => {});
        } catch (e) { /* noop */ }
      } else {
        unbind();
        try { el.srcObject = null; } catch (e) { /* noop */ }
      }
    };
    function onTrackEvent() { attach(); }
    attach();
    rtk.on("videoUpdate", attach);
    // Self-heal: the SDK can swap the underlying track (reconnect / layer change) without a
    // videoUpdate, and Chrome may stop painting after the tab was hidden. Re-check every 2 s
    // and whenever the tab becomes visible again.
    const onVis = () => { if (document.visibilityState === "visible") { if (el.srcObject) el.play().catch(() => {}); attach(); } };
    document.addEventListener("visibilitychange", onVis);
    const id = setInterval(() => {
      const track = rtk.videoTrack;
      const current = el.srcObject?.getVideoTracks?.()[0];
      if (rtk.videoEnabled && track && (current !== track || el.paused)) attach();
      else if (current && (!rtk.videoEnabled || !track)) attach();
    }, 2000);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
      try { rtk.off("videoUpdate", attach); } catch (e) { /* noop */ }
      unbind();
      try { el.srcObject = null; } catch (e) { /* noop */ }
    };
  }, [ref, rtk]);
}

/** Layout-box size (unaffected by CSS transforms, so it works inside the scaled stage). */
function useBoxSize(ref, enabled) {
  const [size, setSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!enabled || !el) return undefined;
    const measure = () => setSize({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref, enabled]);
  return size;
}

/**
 * Reports how many frames really reach this <video> every 2 s: onFlow({ fps, stalledMs }) or
 * onFlow(null) when the camera is off / tab hidden. Cheap (one rVFC per presented frame).
 */
function useFrameFlow(ref, rtk, onFlow) {
  const cbRef = useRef(onFlow);
  cbRef.current = onFlow;
  const wanted = !!onFlow;
  useEffect(() => {
    const el = ref.current;
    if (!el || !rtk || !wanted) return undefined;
    let frames = 0;
    let last = performance.now();
    let handle = null;
    const rvfc = typeof el.requestVideoFrameCallback === "function";
    const onFrame = () => { frames += 1; last = performance.now(); handle = el.requestVideoFrameCallback(onFrame); };
    if (rvfc) handle = el.requestVideoFrameCallback(onFrame);
    let prevTotal = el.getVideoPlaybackQuality?.().totalVideoFrames || 0;
    const id = setInterval(() => {
      let n = frames;
      frames = 0;
      if (!rvfc && el.getVideoPlaybackQuality) {
        const t = el.getVideoPlaybackQuality().totalVideoFrames;
        n = t - prevTotal;
        prevTotal = t;
        if (n > 0) last = performance.now();
      }
      const live = !!(rtk.videoEnabled && rtk.videoTrack) && document.visibilityState === "visible";
      cbRef.current?.(live ? { fps: n / 2, stalledMs: Math.round(performance.now() - last) } : null);
    }, 2000);
    return () => {
      clearInterval(id);
      if (rvfc && handle) { try { el.cancelVideoFrameCallback(handle); } catch (e) { /* noop */ } }
    };
  }, [ref, rtk, wanted]);
}

export function initials(name = "") {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("") || "?";
}

/** Width x height of the video actually flowing for this participant (null if unknown). */
export function trackSize(rtk) {
  try {
    const s = rtk?.videoTrack?.getSettings?.();
    if (s?.width && s?.height) return { w: s.width, h: s.height };
  } catch (e) { /* noop */ }
  return null;
}

/**
 * props:
 *  rtk         RealtimeKit participant or self (may be null => "not connected")
 *  name        display name for the fallback avatar
 *  fit         "cover" | "contain" (overridden by fx.fit)
 *  fx          { mirror?: bool, rotate?: 0|90|180|270, fit?: "cover"|"contain" } producer picture adjustments
 *  showBadges  show mic-muted / cam-off badges (console tiles)
 *  onFlow      optional ({fps, stalledMs} | null) => void, every 2 s — frames actually reaching the tile
 *  className   extra classes for the wrapper
 */
export default function VideoTile({ rtk, name, fit = "cover", fx, showBadges = false, onFlow, className = "", children }) {
  const ref = useRef(null);
  const boxRef = useRef(null);
  useAttachVideo(ref, rtk);
  useFrameFlow(ref, rtk, onFlow);
  const hasVideo = !!(rtk && rtk.videoEnabled);

  const rotate = fx?.rotate || 0;
  const sideways = rotate === 90 || rotate === 270;
  const box = useBoxSize(boxRef, sideways);
  const objectFit = fx?.fit || fit;
  const transform = `${rotate ? `rotate(${rotate}deg)` : ""} ${fx?.mirror ? "scaleX(-1)" : ""}`.trim() || undefined;
  // A sideways video swaps its box: width becomes the tile height and vice versa, centred.
  const videoStyle = sideways && box.w && box.h
    ? { position: "absolute", width: box.h, height: box.w, left: (box.w - box.h) / 2, top: (box.h - box.w) / 2, transform }
    : { transform };

  return (
    <div ref={boxRef} className={`relative overflow-hidden bg-[#0b1220] ${className}`} data-testid="video-tile">
      <video
        ref={ref}
        autoPlay
        playsInline
        muted
        style={videoStyle}
        className={`${sideways ? "" : "absolute inset-0 h-full w-full"} ${objectFit === "contain" ? "object-contain" : "object-cover"} ${hasVideo ? "opacity-100" : "opacity-0"} transition-opacity duration-300`}
      />
      {!hasVideo && (
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#0f1c33] via-[#0b1220] to-[#1a1208]">
          <div className="flex h-[22%] min-h-[40px] aspect-square items-center justify-center rounded-full bg-[#c9772e]/20 text-[#e6a25f] font-semibold" style={{ fontSize: "min(4vw, 40px)" }}>
            {initials(name)}
          </div>
        </div>
      )}
      {showBadges && rtk && (
        <div className="absolute left-1.5 top-1.5 flex gap-1">
          {!rtk.audioEnabled && (
            <span className="rounded bg-red-600/90 p-1 text-white" title="Mic muted"><MicOff className="h-3 w-3" /></span>
          )}
          {!rtk.videoEnabled && (
            <span className="rounded bg-zinc-700/90 p-1 text-white" title="Camera off"><VideoOff className="h-3 w-3" /></span>
          )}
        </div>
      )}
      {children}
    </div>
  );
}
